import React from 'react';

/**
 * TrustpilotBadge Component
 * Displays a Trustpilot review badge/link for social proof
 * 
 * Usage:
 * <TrustpilotBadge variant="inline" />
 * <TrustpilotBadge variant="floating" />
 * <TrustpilotBadge variant="mini" />
 */

const TRUSTPILOT_REVIEW_URL = "https://www.trustpilot.com/review/afriqoma.com";

// Trustpilot brand colors
const TRUSTPILOT_GREEN = "#00b67a";
const TRUSTPILOT_DARK = "#191919";

export const TrustpilotBadge = ({ variant = "inline", className = "" }) => {
  const handleClick = () => {
    window.open(TRUSTPILOT_REVIEW_URL, '_blank', 'noopener,noreferrer');
  };

  // Mini badge - compact version for headers/footers
  if (variant === "mini") {
    return (
      <button
        onClick={handleClick}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-white text-sm font-medium transition-all hover:opacity-90 ${className}`}
        style={{ backgroundColor: TRUSTPILOT_GREEN }}
        data-testid="trustpilot-badge-mini"
      >
        <TrustpilotLogo size={16} />
        <span>Review Us</span>
      </button>
    );
  }

  // Floating badge - fixed position for continuous visibility
  if (variant === "floating") {
    return (
      <button
        onClick={handleClick}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-white font-semibold transition-all hover:scale-105 hover:shadow-xl ${className}`}
        style={{ backgroundColor: TRUSTPILOT_GREEN }}
        data-testid="trustpilot-badge-floating"
      >
        <TrustpilotLogo size={20} />
        <span className="hidden sm:inline">Review us on Trustpilot</span>
        <TrustpilotStars rating={5} size={16} />
      </button>
    );
  }

  // Inline badge - standard display for content sections
  return (
    <div 
      className={`inline-flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 bg-white cursor-pointer hover:shadow-md transition-shadow ${className}`}
      onClick={handleClick}
      data-testid="trustpilot-badge-inline"
    >
      <TrustpilotLogo size={24} />
      <TrustpilotStars rating={5} size={24} />
      <span className="text-sm text-gray-600">Review us on Trustpilot</span>
    </div>
  );
};

// Trustpilot Logo SVG
const TrustpilotLogo = ({ size = 24 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none"
    aria-label="Trustpilot"
  >
    <path 
      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" 
      fill={TRUSTPILOT_GREEN}
    />
  </svg>
);

// Trustpilot Stars Rating Display
export const TrustpilotStars = ({ rating = 5, size = 20 }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <div 
          key={i}
          style={{ 
            width: size, 
            height: size, 
            backgroundColor: i < fullStars ? TRUSTPILOT_GREEN : (i === fullStars && hasHalfStar ? TRUSTPILOT_GREEN : '#dcdce6'),
            clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
          }}
        />
      ))}
    </div>
  );
};

// Trustpilot Review Card - For displaying individual reviews
export const TrustpilotReviewCard = ({ 
  author, 
  rating, 
  title, 
  content, 
  date,
  className = "" 
}) => (
  <div className={`p-4 border border-gray-200 rounded-lg bg-white ${className}`}>
    <div className="flex items-center justify-between mb-2">
      <TrustpilotStars rating={rating} size={16} />
      <span className="text-xs text-gray-500">{date}</span>
    </div>
    <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
    <p className="text-sm text-gray-600 mb-2">{content}</p>
    <p className="text-xs text-gray-500">— {author}</p>
  </div>
);

// Trustpilot CTA Section - For homepage/landing pages
export const TrustpilotCTASection = ({ className = "" }) => (
  <section 
    className={`py-12 px-4 text-center ${className}`}
    style={{ backgroundColor: '#f7f7f7' }}
  >
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        Trusted by Businesses Across Africa
      </h2>
      <p className="text-gray-600 mb-6">
        Join hundreds of verified suppliers and buyers on Africa&apos;s premier B2B marketplace.
        Share your experience and help others discover quality African products.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <TrustpilotBadge variant="inline" />
        <a 
          href={TRUSTPILOT_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium hover:underline"
          style={{ color: TRUSTPILOT_GREEN }}
        >
          See all reviews on Trustpilot →
        </a>
      </div>
    </div>
  </section>
);

// Hook to get Trustpilot configuration from backend
export const useTrustpilotConfig = () => {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trustpilot/config`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (error) {
        console.error('Failed to fetch Trustpilot config:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { config, loading };
};

export default TrustpilotBadge;
